/**
 * BIRHAUS v3.0 Design Tokens
 * Radical minimalist design system with generous spacing, 
 * perfect curves, glass morphism, and delightful animations
 */

// Import all v3 token systems
export * from './borders'
export * from './spacing'
export * from './shadows'
export * from './glass'
export * from './animations'
export * from './typography'

// Import everything we need for the combined objects
import { 
  borderRadiusV3,
  borderWidthV3,
  componentBorders 
} from './borders'

import { 
  spacingV3,
  layoutSpacingV3,
  componentSpacing 
} from './spacing'

import { 
  shadowsV3,
  componentShadows,
  shadowStates 
} from './shadows'

import { 
  glassV3,
  componentGlass,
  glassClasses,
  glassStates 
} from './glass'

import { 
  animationsV3,
  componentAnimations,
  animationUtils,
  performantAnimations 
} from './animations'

import { 
  typographyV3,
  typeScales,
  componentTypography,
  spanishTypography,
  responsiveTypography,
  textColors 
} from './typography'

// Combined v3 token system export
export const tokensV3 = {
  // Border system
  borders: {
    radius: borderRadiusV3,
    width: borderWidthV3,
    components: componentBorders,
  },
  
  // Spacing system
  spacing: {
    scale: spacingV3,
    layout: layoutSpacingV3,
    components: componentSpacing,
  },
  
  // Shadow system
  shadows: {
    scale: shadowsV3,
    components: componentShadows,
    states: shadowStates,
  },
  
  // Glass morphism system
  glass: {
    effects: glassV3,
    components: componentGlass,
    classes: glassClasses,
    states: glassStates,
  },
  
  // Animation system
  animations: {
    scale: animationsV3,
    components: componentAnimations,
    utils: animationUtils,
    performance: performantAnimations,
  },
  
  // Typography system
  typography: {
    scale: typographyV3,
    semantic: typeScales,
    components: componentTypography,
    spanish: spanishTypography,
    responsive: responsiveTypography,
    colors: textColors,
  },
} as const

// Convenience exports for common combinations
export const v3Presets = {
  // Button preset
  button: {
    borderRadius: componentBorders.button,
    padding: componentSpacing.button,
    shadow: componentShadows.button,
    glass: componentGlass.button,
    animation: componentAnimations.button,
    typography: componentTypography.button,
  },
  
  // Card preset
  card: {
    borderRadius: componentBorders.card,
    padding: componentSpacing.card,
    shadow: componentShadows.card,
    glass: componentGlass.card,
    animation: componentAnimations.card,
  },
  
  // Input preset
  input: {
    borderRadius: componentBorders.input,
    padding: componentSpacing.input,
    shadow: componentShadows.input,
    animation: componentAnimations.input,
    typography: componentTypography.input,
  },
  
  // Modal preset
  modal: {
    borderRadius: componentBorders.modal,
    glass: componentGlass.modal,
    shadow: componentShadows.modal,
    animation: componentAnimations.modal,
  },
} as const

export default tokensV3